import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell } from 'lucide-react';
import { FEATURES } from '../../config';
import {
  enableWebPushFromUserGesture,
  PUSH_OPT_IN_DISMISSED_KEY,
} from '../../services/pushService';
import { isRunningAsInstalledPwa, subscribeInstalledPwaDisplayMode } from '../../utils/pwaInstallEnvironment';
import { isWebPushSupported } from '../../utils/webPush';
import { PWA_UPDATE_BANNER_VISIBILITY_EVENT } from '../../utils/pwaUpdateBannerEvents';

const ANIMATION_DURATION_MS = 280;

interface PwaPushPermissionBannerProps {
  user?: { pushNotifications?: boolean; PushNotifications?: boolean } | null;
}

const PwaPushPermissionBanner: React.FC<PwaPushPermissionBannerProps> = ({ user }) => {
  const { t } = useTranslation('common');
  const [visible, setVisible] = useState(false);
  const [animatingIn, setAnimatingIn] = useState(false);
  const [updateBannerVisible, setUpdateBannerVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [installed, setInstalled] = useState(() => isRunningAsInstalledPwa());

  useEffect(() => {
    const refresh = () => setInstalled(isRunningAsInstalledPwa());
    refresh();
    return subscribeInstalledPwaDisplayMode(refresh);
  }, []);

  const shouldOffer = Boolean(
    FEATURES.enablePushNotifications &&
      user &&
      installed &&
      isWebPushSupported() &&
      typeof Notification !== 'undefined' &&
      Notification.permission === 'default' &&
      localStorage.getItem(PUSH_OPT_IN_DISMISSED_KEY) !== '1'
  );

  useEffect(() => {
    const onUpdateBanner = (event: Event) => {
      const detail = (event as CustomEvent<{ visible?: boolean }>).detail;
      setUpdateBannerVisible(Boolean(detail?.visible));
    };
    window.addEventListener(PWA_UPDATE_BANNER_VISIBILITY_EVENT, onUpdateBanner as EventListener);
    return () => window.removeEventListener(PWA_UPDATE_BANNER_VISIBILITY_EVENT, onUpdateBanner as EventListener);
  }, []);

  useEffect(() => {
    if (!shouldOffer || updateBannerVisible) {
      setAnimatingIn(false);
      const timeoutId = window.setTimeout(() => setVisible(false), ANIMATION_DURATION_MS);
      return () => window.clearTimeout(timeoutId);
    }
    setVisible(true);
    const frame = requestAnimationFrame(() => setAnimatingIn(true));
    return () => cancelAnimationFrame(frame);
  }, [shouldOffer, updateBannerVisible]);

  const hide = useCallback(() => {
    setAnimatingIn(false);
    window.setTimeout(() => setVisible(false), ANIMATION_DURATION_MS);
  }, []);

  const dismiss = useCallback(() => {
    localStorage.setItem(PUSH_OPT_IN_DISMISSED_KEY, '1');
    hide();
  }, [hide]);

  const enable = useCallback(async () => {
    setBusy(true);
    try {
      const result = await enableWebPushFromUserGesture();
      if (result === 'subscribed' || result === 'denied' || result === 'unsupported') {
        hide();
      }
    } finally {
      setBusy(false);
    }
  }, [hide]);

  if (!visible) return null;

  return (
    <div
      className={`fixed left-3 right-3 bottom-3 z-[10001] transform transition-all duration-300 ease-out sm:left-6 sm:right-6 sm:bottom-4 lg:left-1/2 lg:right-auto lg:w-[28rem] lg:-translate-x-1/2 ${
        animatingIn ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
      }`}
    >
      <div className="rounded-2xl border border-amber-200/70 bg-white/90 p-4 shadow-2xl backdrop-blur-md dark:border-amber-800/50 dark:bg-black/85">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200">
            <Bell className="h-5 w-5" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {t('push.permissionTitle', { defaultValue: 'Get message alerts' })}
            </p>
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-300 sm:text-sm">
              {t('push.permissionBody', { defaultValue: 'Allow notifications to see new messages even when the app is closed.' })}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void enable()}
                disabled={busy}
                className="rounded-lg bg-amber-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-60"
              >
                {t('push.permissionEnable', { defaultValue: 'Allow' })}
              </button>
              <button
                type="button"
                onClick={dismiss}
                className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-black/5 dark:text-gray-300 dark:hover:bg-white/10"
              >
                {t('push.permissionLater', { defaultValue: 'Not now' })}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PwaPushPermissionBanner;
